/**
 * errorlog.ts — the game's first error telemetry (the loading round).
 *
 * The audit found zero global error handling: an exception inside a race
 * start left the player on the attract flythrough with NOTHING logged
 * anywhere. This module installs window 'error' + 'unhandledrejection'
 * handlers, keeps a small ring of recent errors in localStorage (so a "it
 * just didn't load" report can be followed by a console read on the device),
 * and exposes the ring for the online diag report (netdiag → {t:'diag'}),
 * which the room server logs verbatim — free, no new infrastructure.
 */

export interface ErrorRecord {
  t: number; // epoch ms
  where: string;
  msg: string;
  stack?: string;
}

const KEY = 'dribble-racer.errors.v1';
const MAX = 20;
let ring: ErrorRecord[] | null = null;
let installed = false;

function load(): ErrorRecord[] {
  if (ring) return ring;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    ring = raw ? (JSON.parse(raw) as ErrorRecord[]) : [];
  } catch {
    ring = [];
  }
  return ring;
}

function persist(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(load()));
  } catch {
    /* quota / private mode — the in-memory ring still works */
  }
}

/** Record an error (never throws). `where` names the code path. */
export function reportError(where: string, err: unknown): void {
  try {
    const e = err as { message?: unknown; stack?: unknown } | null;
    const msg =
      typeof err === 'string' ? err : e && typeof e.message === 'string' ? e.message : String(err);
    const rec: ErrorRecord = {
      t: Date.now(),
      where,
      msg: msg.slice(0, 300),
    };
    if (e && typeof e.stack === 'string') rec.stack = e.stack.slice(0, 600);
    const r = load();
    r.push(rec);
    while (r.length > MAX) r.shift();
    persist();
  } catch {
    /* reporting must never be the thing that breaks */
  }
}

/** The most recent records (newest last), for the diag report / debugging. */
export function recentErrors(max = 5): ErrorRecord[] {
  const r = load();
  return r.slice(Math.max(0, r.length - max));
}

/** Errors recorded since the given epoch ms — the race-scoped subset. */
export function errorsSince(t: number): ErrorRecord[] {
  return load().filter((e) => e.t >= t);
}

/** Install the global handlers once. Safe to call repeatedly. */
export function installErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (ev) => {
    reportError('window.error', ev.error ?? ev.message);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    reportError('unhandledrejection', ev.reason);
  });
}

/** Debug handle (?debug=1): window.__errors() */
export function exposeErrors(): void {
  (window as unknown as { __errors?: () => ErrorRecord[] }).__errors = () => recentErrors(20);
}
